/*
 * Copyright (c) 2021 SARL SIGMA POLYNESIA
 */

/**
 * Returns the number of days for each month between two dates.
 * @param {DateTime} startDateTime
 * @param {DateTime} endDateTime
 * @return {number[]}
 */
export function getDaysPerMonthBetweenDates (startDateTime, endDateTime) {
  const list = []
  let dt = startDateTime.startOf('month')

  if (startDateTime.hasSame(endDateTime, 'month')) {
    list.push(endDateTime.day - startDateTime.day + 1)
  } else {
    while (dt < endDateTime) {
      if (dt.hasSame(endDateTime, 'month')) {
        list.push(endDateTime.day)
      } else if (dt.hasSame(startDateTime, 'month')) {
        list.push(dt.daysInMonth - startDateTime.day + 1)
      } else {
        list.push(dt.daysInMonth)
      }
      dt = dt.plus({ month: 1 })
    }
  }
  return list
}

/**
 * Calculates a prorated amount.
 * @param amount {number}
 * @param startDateTime {DateTime}
 * @param endDateTime {DateTime}
 * @param unit {string}
 * @returns {{
 * daysInPeriod: number,
 * daysAtProrataFormula: string,
 * amountAtProrataFormula: string,
 * amountPerDayFormula: string,
 * startDay: number,
 * daysAtProrata: number,
 * endDay: number,
 * amountPerDay: number,
 * amountAtProrata: number,
 * ratio: number
 * }}
 */
export function calculateProrata (
  amount,
  startDateTime,
  endDateTime,
  unit = 'month'
) {
  if (!startDateTime.isValid) {
    throw new Error(startDateTime.invalidReason)
  }
  if (!endDateTime.isValid) {
    throw new Error(endDateTime.invalidReason)
  }

  let { days: daysInPeriod } = endDateTime.endOf(unit)
    .diff(startDateTime.startOf(unit), 'days')

  daysInPeriod = Math.round(daysInPeriod)

  const result = {
    daysInPeriod,
    daysAtProrata: null,
    daysAtProrataFormula: null,
    amountPerDay: null,
    amountPerDayFormula: null,
    amountAtProrata: null,
    amountAtProrataFormula: null,
    ratio: null
  }

  let { daysAtProrata, daysAtProrataFormula } = result

  if (startDateTime.hasSame(endDateTime, 'month')) {
    const startDay = startDateTime.day
    const endDay = endDateTime.day
    daysAtProrata = endDay - startDay + 1
    daysAtProrataFormula = `${endDay} - ${startDay} + 1 = ${daysAtProrata}`
  } else {
    const daysPerMonth = getDaysPerMonthBetweenDates(startDateTime, endDateTime)
    daysAtProrata = daysPerMonth.reduce((a, b) => a + b, 0)
    daysAtProrataFormula = `${daysPerMonth.join(' + ')} = ${daysAtProrata}`
  }

  const amountPerDay = amount / daysInPeriod
  const amountPerDayFormula = `${amount} / ${daysInPeriod} = ${amountPerDay.toFixed(
    2)}`

  const amountAtProrata = amountPerDay * daysAtProrata
  const amountAtProrataFormula = `${amountPerDay.toFixed(2)} x ${daysAtProrata} = ${amountAtProrata.toFixed(
    2)}`

  result.daysAtProrata = daysAtProrata
  result.daysAtProrataFormula = daysAtProrataFormula
  result.amountPerDay = amountPerDay
  result.amountPerDayFormula = amountPerDayFormula
  result.amountAtProrata = amountAtProrata
  result.amountAtProrataFormula = amountAtProrataFormula
  result.ratio = amountAtProrata / amount

  return result
}
